import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Wizard } from 'react-use-wizard';

import { updateBatch } from '@/features/single-measurement/services/batches';
import { IBatch, State } from '@/features/single-measurement/types';
import { useRoute } from '@/hooks/use-route';

import { Modal } from '../../../../components/common/modal';
import { CreateBatchStep } from './steps/create-batch-step';
import { MethodSelectionStep } from './steps/method-selection-setp';
import { StepState } from './types';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** callback to set 'isOpen' to false, function will be called when user need to close the modal */
  close: () => void;

  /** if you pass the fermentation id, batch will be added to the relevant fermentation  */
  fermentationId?: string;

  /** callback upon a successful creation */
  onSuccess?: (data: IBatch) => void;
}

export const CreateAnalysisWizard: React.FC<IProps> = ({
  close,
  isOpen,
  onSuccess,
  fermentationId,
}) => {
  const { t } = useTranslation();
  const { routeToAnalysis } = useRoute();
  const [state, setState] = useState<StepState>({ method: null });

  const deleteBatchMutation = useMutation({
    mutationFn: (id: string) => updateBatch(id, { state: State.AbortedByUser }),
    onSuccess: () => {
      toast.success(t('measurements.delete.success'));
    },
  });

  // delete batch on close or back event
  const handleOnReset = () => {
    state.batchId && deleteBatchMutation.mutate(state.batchId);
  };

  const handleClose = () => {
    handleOnReset();
    close();
  };

  // if onSuccess haven't defined, by default we redirect to the single measurment page
  const handleOnSuccess = (data: IBatch) => {
    if (typeof onSuccess === 'undefined') routeToAnalysis(data.id);
    onSuccess && onSuccess(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      title={t('measurements.create.new')}
      conatinerClassnames="w-full 2xl:w-2/3 overflow-y-auto max-h-screen"
    >
      <Wizard>
        <MethodSelectionStep
          state={state}
          setState={setState}
          fermentationId={fermentationId}
        />
        <CreateBatchStep
          state={state}
          setState={setState}
          onReset={handleOnReset}
          onSuccess={handleOnSuccess}
        />
      </Wizard>
    </Modal>
  );
};
